import { Alert, Box, Button, Stack } from '@mui/material';
import React, { useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { RegistryAllocation, RegistryEntityDetail } from '../../api/registryEntityTypes';
import { ENTITY_TYPE_LABELS, isJointHolding } from '../../entities/entityFormat';
import { sampleHolderPath } from '../../entities/entityPaths';
import { useEntitySource } from '../../entities/useEntitySource';
import { useSourceQuery } from '../../entities/useSourceQuery';
import { useTenantScope } from '../../store/selectors';
import { PageHeader, SectionCard, StatusChip } from '../../ui/primitives';
import AllocationsTable from './AllocationsTable';
import EntityContactsCard from './EntityContactsCard';
import EntityDetailsCard from './EntityDetailsCard';
import SourceNotice, { QueryStatus } from './SourceNotice';

function RegistryRecordCard({ entityId }: { entityId: string }) {
  const holder = useTenantScope().holderByEntityId.get(entityId);
  return (
    <SectionCard title="Registry record (sample)">
      <Alert severity="info">
        Holder number, TFN, CRS/FATCA, risk rating, beneficial owners and their checks are registry
        data that does not exist yet (milestone 7); the prototype shows them on sample holders.
      </Alert>
      {holder && (
        <Button component={RouterLink} to={sampleHolderPath(holder.id)} sx={{ mt: 1.5 }}>
          Open sample record {holder.accountNumber}
        </Button>
      )}
    </SectionCard>
  );
}

function EntityView({ entity, sample }: { entity: RegistryEntityDetail; sample?: boolean }) {
  const owners = entity.contacts.filter((contact) => contact.role === 'OWNER');
  const ownerEmail = (allocation: RegistryAllocation) =>
    entity.contacts.find((contact) => contact.externalId === allocation.contact.externalId)
      ?.email ?? undefined;
  return (
    <>
      <PageHeader
        eyebrow={`${ENTITY_TYPE_LABELS[entity.type]} · investment entity`}
        title={entity.name}
        subtitle={
          <Stack
            useFlexGap
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', rowGap: 1, mt: 0.5 }}
          >
            {isJointHolding(owners.length) && (
              <StatusChip status="OPEN" label={`Joint holding · ${owners.length} owners`} />
            )}
            {owners.length === 0 && <StatusChip status="PENDING" label="No owner linked" />}
            {entity.isForeignInvestor && (
              <StatusChip status="REPORTABLE" label="Foreign investor" />
            )}
          </Stack>
        }
        actions={
          <Button component={RouterLink} to="/manager/holders" variant="outlined">
            All holders
          </Button>
        }
      />
      <SourceNotice sample={sample} />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: '1fr 1fr' }, mb: 2 }}>
        <EntityDetailsCard entity={entity} />
        <EntityContactsCard contacts={entity.contacts} />
      </Box>
      <SectionCard
        title={`Allocations (${entity.allocations.length})`}
        subtitle="Investor allocations to this entity, made in the admin portal. An application is based on an allocation."
        noPadding
        sx={{ mb: 2 }}
      >
        <AllocationsTable
          allocations={entity.allocations}
          groupBy="fundOpportunity"
          ownerEmail={ownerEmail}
        />
      </SectionCard>
      <RegistryRecordCard entityId={entity.externalId} />
    </>
  );
}

export default function EntityDetailPage() {
  const { entityId = '' } = useParams();
  const source = useEntitySource();
  const load = useCallback(() => source.getEntity(entityId), [source, entityId]);
  const query = useSourceQuery(load);
  return (
    <QueryStatus query={query}>
      {(entity) => <EntityView entity={entity} sample={source.sample} />}
    </QueryStatus>
  );
}
